import React, { useState, useMemo, useEffect, useCallback } from "react";

const PARTS = [
  {
    id: "foundations",
    n: 1,
    title: "Foundations",
    blurb:
      "The vocabulary. Everything after this assumes you can read a model definition and predict what it will and will not accept.",
    concepts: [
      {
        id: "what-is",
        title: "What Pydantic is, and why it earns its place",
        explain:
          "Pydantic is a data validation and parsing library. You describe the shape of your data with ordinary Python type hints on a class, and Pydantic compiles that into three things at once: a runtime validator, a parser that converts input into real Python objects, and a JSON Schema generator. The engine underneath is pydantic-core, written in Rust, which is why v2 is fast enough to sit on a hot request path. It belongs at every boundary where data you do not control enters code you do.",
        simple: `from pydantic import BaseModel

class Server(BaseModel):
    site_code: str
    port: int

s = Server(site_code="MH-PUN-01", port="8443")
print(s.port, type(s.port))   # 8443 <class 'int'>  -- parsed, not just checked`,
        real: `# The three boundaries where Pydantic pays for itself:
#
#   HTTP request body   ->  ServerCreate          (untrusted client input)
#   Environment / files ->  Settings(BaseSettings) (untrusted ops input)
#   DB row / vendor API ->  ServerResponse        (input you did not shape)
#
# Everything strictly inside those boundaries can stay a plain object.
# If data never crosses a boundary, validating it is pure cost.`,
        mistakes: [
          "Treating it as an ORM. Pydantic does not talk to a database — it validates the objects that go to and come from one.",
          "Validating the same payload at three layers. Validate once, at the edge, then pass the typed object inward.",
          "Reaching for BaseModel for internal-only value objects where a dataclass or NamedTuple is cheaper and enough.",
        ],
        exercise:
          "Define a model with three fields (str, int, bool). Feed it a dict where every value is a string. Print the result and the type of each attribute. Note which conversions Pydantic made silently.",
      },
      {
        id: "hints-vs-validation",
        title: "Python type hints vs Pydantic validation",
        explain:
          "Type hints are inert at runtime — the interpreter never checks them. A static checker like mypy reads them before your code runs. Pydantic reads the same hints and enforces them while your code runs. These are two different halves of one problem, and neither replaces the other. mypy cannot protect an API boundary because the payload does not exist until runtime; Pydantic cannot catch a typo in code that never executes.",
        simple: `def get_port(raw: str) -> int:
    return raw            # mypy complains. Python does not. Runtime: no error.

# Pydantic is the runtime half:
from pydantic import BaseModel

class Cfg(BaseModel):
    port: int

Cfg(port="not-a-number")   # ValidationError, at runtime, at the boundary`,
        real: `# Why a type checker alone is not enough for a REST service:
#
#   @app.post("/servers")
#   def create(payload: ServerCreate): ...
#
# mypy proves your handler USES payload correctly.
# It cannot prove the client SENT something that matches ServerCreate.
# That JSON arrives at runtime, from a machine you do not control.`,
        mistakes: [
          '"We use mypy, so we do not need runtime validation." The two cover disjoint failure modes.',
          '"We use Pydantic, so we do not need mypy." Pydantic will not tell you that you spelled an attribute wrong in a branch that only runs at 3am.',
        ],
        exercise:
          "Write one function annotated `-> int` that returns a string. Run mypy on it, then run it. Then do the same thing through a BaseModel and compare where the failure surfaces.",
      },
      {
        id: "basemodel",
        title: "BaseModel and model fields",
        v2: "`.dict()` → `.model_dump()`, `.json()` → `.model_dump_json()`, `parse_obj()` → `model_validate()`, `class Config` → `model_config`.",
        explain:
          "Subclass BaseModel and annotate class attributes. Every annotated attribute becomes a field; Pydantic generates `__init__`, `__eq__` and `__repr__`, and validates on construction. An attribute with no annotation is not a field at all — it stays a plain class variable, which is a surprisingly common source of silently-ignored config.",
        simple: `from pydantic import BaseModel

class Server(BaseModel):
    site_code: str
    port: int = 8443
    tags: list[str] = []       # safe here: Pydantic deep-copies defaults per instance

    VENDOR = "dell"            # NO annotation -> class variable, not a field`,
        real: `from enum import Enum
from pydantic import BaseModel

class SiteType(str, Enum):
    EDGE = "edge"
    CORE = "core"
    DC = "datacenter"

class Server(BaseModel):
    site_name: str
    site_code: str
    site_type: SiteType
    region: str
    circle: str
    platform: str
    version: str

s = Server.model_validate({
    "site_name": "Pune Edge 04", "site_code": "MH-PUN-04",
    "site_type": "edge", "region": "west", "circle": "MH",
    "platform": "ocp", "version": "4.20",
})`,
        mistakes: [
          "Forgetting the annotation. `VENDOR = \"dell\"` is not a field and will not be validated, serialized or documented.",
          "Expecting the dataclass mutable-default trap. Pydantic deep-copies defaults per instance, so `tags: list[str] = []` is actually safe — but `default_factory=list` states the intent more clearly.",
          "Assuming assignment is validated. `s.port = \"abc\"` succeeds unless you set `model_config = ConfigDict(validate_assignment=True)`.",
        ],
        exercise:
          "Build the Server model above, then add an un-annotated class attribute and confirm it does not appear in `model_dump()` or `model_json_schema()`.",
      },
      {
        id: "required-optional",
        title: "Required, optional and default values",
        v2: "Big one: in v1, `x: Optional[str]` implicitly defaulted to None. In v2 it is still REQUIRED — it just also accepts None. Write `x: str | None = None` if you want it optional.",
        explain:
          "A field is required when it has no default, full stop. The type has nothing to do with it. `str | None` means \"this may hold None\", not \"this may be missing\". Optionality comes from a default; nullability comes from the type. Keeping those two ideas apart removes most of the confusion beginners have with Pydantic.",
        simple: `from pydantic import BaseModel

class A(BaseModel):
    a: str                 # required
    b: str | None          # required, but may be None      <- v2 change
    c: str | None = None   # optional, defaults to None
    d: str = "edge"        # optional, defaults to "edge"

A(a="x")                   # ValidationError: b is missing`,
        real: `from pydantic import BaseModel

class ServerUpdate(BaseModel):
    """PATCH body: every field omittable, but None must stay meaningful."""
    site_name: str | None = None
    version:   str | None = None
    circle:    str | None = None

body = ServerUpdate(site_name="Pune Edge 04")

# exclude_unset is what separates "not sent" from "sent as null"
body.model_dump(exclude_unset=True)   # {'site_name': 'Pune Edge 04'}
body.model_dump()                     # all three keys, two of them None`,
        mistakes: [
          "Porting a v1 model and losing required-ness: every `Optional[x]` field silently becomes required in v2.",
          "Using `exclude_none=True` on a PATCH model. That throws away a deliberate `null`, which is a real instruction to clear a column. `exclude_unset=True` is almost always what you want.",
          "`Field(...)` with a literal Ellipsis to mean required. Correct, but omitting the default says the same thing more plainly.",
        ],
        exercise:
          "Build a three-field PATCH model. Send `{\"version\": null}` and `{}`. Print `model_dump(exclude_unset=True)` for both and confirm you can distinguish them.",
      },
      {
        id: "coercion",
        title: "Type coercion and strict validation",
        v2: "v2 tightened coercion sharply. `int -> str` is no longer allowed. Union resolution moved from left-to-right coercion to smart mode.",
        explain:
          "By default Pydantic runs in lax mode: it converts input when the conversion is unambiguous and lossless. The string \"8443\" becomes 8443; the string \"abc\" raises. Strict mode turns conversion off and demands the exact type. You can set it per field, per model, or per call, and the rules differ between Python input and JSON input — JSON has no datetime type, so a date string is accepted in strict JSON mode but a `str` is not accepted for a `datetime` field in strict Python mode.",
        simple: `from pydantic import BaseModel, Field, ConfigDict

class Lax(BaseModel):
    port: int

Lax(port="8443").port          # 8443   -- converted

class Strict(BaseModel):
    model_config = ConfigDict(strict=True)
    port: int

Strict(port="8443")            # ValidationError: Input should be a valid integer

# per field instead of per model:
class Mixed(BaseModel):
    port: int = Field(strict=True)
    retries: int                      # still lax`,
        real: `# Rule of thumb by boundary:
#
#   Env vars, query params, CSV, form data   -> LAX.
#       Everything arrives as a string; coercion is the whole point.
#
#   Internal service-to-service, DB rows     -> STRICT.
#       Both sides are your code. A type mismatch is a bug, not a format.
#
# Per-call is often cleanest:
Server.model_validate(row, strict=True)`,
        mistakes: [
          "Relying on coercion for money. `Decimal` from a float is lossy — take the string.",
          "Being surprised that `1` and `0` validate as `bool` in lax mode. They do, along with \"yes\"/\"no\"/\"on\"/\"off\".",
          "Turning on strict mode globally, then finding every query parameter broken because HTTP has only strings.",
        ],
        exercise:
          "Take one model and validate the same payload three ways: lax, `strict=True` per call, and `ConfigDict(strict=True)`. Note which inputs survive each.",
      },
      {
        id: "nested",
        title: "Nested models",
        explain:
          "A field annotated with another BaseModel is validated recursively. You can pass a dict and Pydantic will build the child model for you. Errors from deep inside carry a `loc` tuple giving the full path, which is what lets you return a per-field API error rather than \"something was wrong\".",
        simple: `from pydantic import BaseModel

class Location(BaseModel):
    city: str
    region: str

class Server(BaseModel):
    site_code: str
    location: Location

s = Server(site_code="MH-PUN-04",
           location={"city": "Pune", "region": "west"})   # dict is fine
print(s.location.city)`,
        real: `from pydantic import BaseModel, ValidationError

class NIC(BaseModel):
    name: str
    ip: str
    mtu: int = 1500

class Server(BaseModel):
    site_code: str
    location: Location
    interfaces: list[NIC] = []

try:
    Server.model_validate({
        "site_code": "MH-PUN-04",
        "location": {"city": "Pune", "region": "west"},
        "interfaces": [{"name": "eno1", "ip": "10.0.0.1", "mtu": "big"}],
    })
except ValidationError as e:
    print(e.errors()[0]["loc"])   # ('interfaces', 0, 'mtu')  <- exact path`,
        mistakes: [
          "Building the child model by hand before passing it in. Unnecessary — a dict validates fine and reads better.",
          "Nesting five levels deep because the JSON does. Model what your code needs, not what the vendor sent.",
          "Throwing away the `loc` path when converting errors for an API response. That path is the single most useful thing in the error.",
        ],
        exercise:
          "Build the three-level model above, send a bad value in the innermost field, and print the full `loc` tuple. Then write a one-line helper that turns that tuple into a dotted path like `interfaces.0.mtu`.",
      },
      {
        id: "collections",
        title: "Lists, dicts, tuples and sets",
        v2: "`min_items`/`max_items` are now `min_length`/`max_length`.",
        explain:
          "Container types are validated element by element. A bare `list` means `list[Any]` and validates nothing inside, so always parameterise. Fixed-length tuples are written `tuple[float, float]`; variable-length as `tuple[str, ...]`. Sets deduplicate, which is either exactly what you want or a silent data loss — decide deliberately.",
        simple: `from typing import Annotated
from pydantic import BaseModel, Field

class M(BaseModel):
    tags: set[str] = set()                              # deduplicates
    labels: dict[str, str] = {}
    coords: tuple[float, float]                         # exactly two
    versions: list[str] = []
    ports: Annotated[list[int], Field(min_length=1, max_length=8)]`,
        real: `from typing import Annotated
from pydantic import BaseModel, Field

Port = Annotated[int, Field(ge=1, le=65535)]

class Server(BaseModel):
    labels: dict[str, str] = {}                 # k8s-style labels
    exposed_ports: list[Port] = []              # constraint applies per element
    coords: tuple[float, float] | None = None   # lat, lon

Server(exposed_ports=[80, 70000])
# ValidationError, loc = ('exposed_ports', 1)`,
        mistakes: [
          "Bare `list` or `dict`. It parses as `Any` inside and gives you nothing.",
          "Using `set` for something order-sensitive, then wondering why the UI list keeps reshuffling.",
          "Applying `min_length` to the list when you meant the string elements. Put element constraints on the element type via Annotated.",
        ],
        exercise:
          "Model a `labels: dict[str, str]` where keys must be at most 63 characters. Work out where that constraint has to go.",
      },
      {
        id: "union-literal-enum",
        title: "Union, Literal and Enum",
        v2: "Unions use smart mode — best match wins, not first match. v1 tried each member left to right and coerced into the first that fit, which quietly mangled data.",
        explain:
          "Three ways to say \"one of a set\". Literal is for a small, closed, inline set of values. Enum is for a named domain concept you reference in more than one place. Union is for values that may be one of several shapes — and once the members are models, you almost always want a discriminated union instead (part 4).",
        simple: `from typing import Literal
from enum import Enum
from pydantic import BaseModel

class Status(str, Enum):
    ACTIVE = "active"
    MAINTENANCE = "maintenance"
    DECOMMISSIONED = "decommissioned"

class Server(BaseModel):
    site_type: Literal["edge", "core", "datacenter"]
    status: Status
    mtu: int | Literal["auto"] = "auto"`,
        real: `# Why str-Enum and not bare str:
#
#   Status.ACTIVE is one name, checked by mypy, rendered in OpenAPI,
#   and comparable to the string "active" because it subclasses str.
#
# Why Literal and not Enum for site_type:
#
#   Three values, used in one model, never referenced elsewhere.
#   An Enum here is a file you have to open to read the model.

s = Server(site_type="edge", status="active")
s.status is Status.ACTIVE       # True
s.status == "active"            # True, thanks to str mixin`,
        mistakes: [
          "Plain `str` for a closed set, then a typo reaching the database. Literal or Enum costs nothing and stops it.",
          "A 200-member Literal. It bloats the JSON schema and the OpenAPI page. Validate against a lookup instead.",
          "`class Status(Enum)` without the `str` mixin, then finding it does not serialize to JSON cleanly or compare to strings.",
        ],
        exercise:
          "Define `status` as a plain str-Enum and dump the model to JSON. Then remove the `str` mixin and dump again. Explain the difference.",
      },
      {
        id: "annotated",
        title: "Annotated",
        v2: "The v2 idiom. Constraints attach to the TYPE, not to the field, which is what makes them reusable.",
        explain:
          "`Annotated[T, ...metadata]` bolts extra information onto a type without changing it. Pydantic reads that metadata for constraints and validators. This is the mechanism behind every reusable domain type you will write, and it is why v2 code has a `types.py` instead of the same `Field(ge=1, le=65535)` copy-pasted into fourteen models.",
        simple: `from typing import Annotated
from pydantic import BaseModel, Field

Port = Annotated[int, Field(ge=1, le=65535)]

class A(BaseModel):
    api_port: Port
    metrics_port: Port = 9100`,
        real: `# app/types.py -- written once
from typing import Annotated
from pydantic import Field, BeforeValidator, AfterValidator

def _upper(v: str) -> str:
    return v.strip().upper() if isinstance(v, str) else v

def _valid_circle(v: str) -> str:
    if v not in {"MH", "DL", "TN", "KA", "GJ"}:
        raise ValueError(f"unknown circle {v!r}")
    return v

SiteCode = Annotated[str, Field(pattern=r"^[A-Z]{2}-[A-Z]{3}-\\d{2}$"), BeforeValidator(_upper)]
Circle   = Annotated[str, BeforeValidator(_upper), AfterValidator(_valid_circle)]
Port     = Annotated[int, Field(ge=1, le=65535)]

# app/schemas/server.py -- read everywhere
class Server(BaseModel):
    site_code: SiteCode
    circle: Circle
    api_port: Port = 8443`,
        mistakes: [
          "Repeating `Field(ge=1, le=65535)` in every model instead of naming the type once.",
          "Writing both `x: Annotated[int, Field(ge=1)] = Field(default=3)`. Pick one place for the metadata; the default can stay as a plain `= 3`.",
          "Putting a `description` in Annotated metadata for a field whose meaning changes per model. That one belongs on the field.",
        ],
        exercise:
          "Create `Port`, `Hostname` and `SiteCode` as Annotated aliases in one module. Use all three in two different models and confirm the constraints show up in `model_json_schema()`.",
      },
      {
        id: "field",
        title: "Field()",
        v2: "`regex` → `pattern`, `min_items` → `min_length`, `allow_mutation=False` → `frozen=True`, `const` removed (use Literal).",
        explain:
          "Field() carries everything about a field that the type itself cannot express: defaults, numeric and string constraints, aliases, documentation, and per-field config. Its documentation arguments are not decoration — `description` and `examples` flow into the JSON schema and from there into your OpenAPI page.",
        simple: `from pydantic import BaseModel, Field

class Server(BaseModel):
    site_code: str = Field(
        pattern=r"^[A-Z]{2}-[A-Z]{3}-\\d{2}$",
        description="Circle-city-index code, e.g. MH-PUN-04",
        examples=["MH-PUN-04"],
    )
    api_port: int = Field(default=8443, ge=1, le=65535)
    tags: list[str] = Field(default_factory=list)
    secret: str = Field(exclude=True)         # never serialized`,
        real: `from pydantic import BaseModel, Field

class ServerCreate(BaseModel):
    site_name: str = Field(min_length=3, max_length=120)
    site_code: str = Field(alias="siteCode")               # wire name differs
    api_port: int = Field(8443, ge=1, le=65535,
                          description="HTTPS port for the platform API")
    internal_note: str | None = Field(None, exclude=True)  # in, never out
    row_version: int = Field(0, frozen=True)               # set once`,
        mistakes: [
          "`default` and `default_factory` together. That is an error — choose one.",
          "`Field(...)` with a literal Ellipsis when you could simply omit the default.",
          "Leaving `description` empty and then wondering why the generated OpenAPI docs are useless.",
          "Assuming `exclude=True` also blocks input. It only affects serialization; the field still validates on the way in.",
        ],
        exercise:
          "Add `description` and `examples` to three fields, then print `model_json_schema()` and find them. That is exactly what FastAPI renders.",
      },
    ],
  },
  {
    id: "validation",
    n: 2,
    title: "Model validation",
    blurb:
      "Six mechanisms that overlap. The skill is not learning all six — it is knowing which one a given rule belongs in.",
    concepts: [
      {
        id: "field-validator",
        title: "field_validator",
        v2: "Replaces v1's `@validator`. `pre=True` → `mode=\"before\"`, `each_item=True` → put the validator on the element type via Annotated.",
        explain:
          "A classmethod that runs for one or more named fields. Default mode is `\"after\"`, meaning it runs once Pydantic has already parsed the value, so the argument is guaranteed to be the declared type. Return the value — a validator that returns None silently blanks the field.",
        simple: `from pydantic import BaseModel, field_validator

class Server(BaseModel):
    site_code: str

    @field_validator("site_code")
    @classmethod
    def normalise(cls, v: str) -> str:
        return v.strip().upper()

Server(site_code="  mh-pun-04 ").site_code   # 'MH-PUN-04'`,
        real: `from pydantic import BaseModel, field_validator

KNOWN_CIRCLES = {"MH", "DL", "TN", "KA", "GJ"}

class Server(BaseModel):
    site_code: str
    circle: str
    version: str

    @field_validator("site_code", "circle", mode="before")
    @classmethod
    def upper(cls, v):
        return v.upper() if isinstance(v, str) else v

    @field_validator("circle")
    @classmethod
    def known_circle(cls, v: str) -> str:
        if v not in KNOWN_CIRCLES:
            raise ValueError(f"unknown circle {v!r}; expected one of {sorted(KNOWN_CIRCLES)}")
        return v

    @field_validator("version")
    @classmethod
    def semver_ish(cls, v: str) -> str:
        if not all(p.isdigit() for p in v.split(".")):
            raise ValueError("version must be numeric, e.g. 4.20")
        return v`,
        mistakes: [
          "Forgetting `@classmethod`. It works by accident in some cases and confuses everyone reading it.",
          "Forgetting to return the value. The field becomes None with no error.",
          "Raising `HTTPException` inside a validator. That drags the web layer into your schema layer — raise `ValueError` and let the API layer translate it.",
          "Using it for cross-field rules. A field_validator only reliably sees fields declared *above* it. Use `model_validator` instead.",
        ],
        exercise:
          "Write a validator that uppercases `circle` in `before` mode and a second that checks membership in `after` mode. Confirm that a lowercase input passes both.",
      },
      {
        id: "model-validator",
        title: "model_validator",
        v2: "Replaces `@root_validator`. `pre=True` → `mode=\"before\"`; the after-mode form is an instance method that returns `self`.",
        explain:
          "Runs on the whole model rather than one field. `mode=\"before\"` receives the raw input before any field validation — useful for reshaping a payload. `mode=\"after\"` receives the constructed instance with every field already validated, and is where cross-field business rules belong.",
        simple: `from pydantic import BaseModel, model_validator

class Window(BaseModel):
    start_hour: int
    end_hour: int

    @model_validator(mode="after")
    def check_order(self):
        if self.start_hour >= self.end_hour:
            raise ValueError("start_hour must be before end_hour")
        return self          # after-mode MUST return self`,
        real: `from datetime import datetime
from pydantic import BaseModel, model_validator

class Server(BaseModel):
    status: str
    decommissioned_at: datetime | None = None
    replacement_site_code: str | None = None

    @model_validator(mode="before")
    @classmethod
    def flatten_legacy(cls, data):
        if isinstance(data, dict) and "meta" in data:
            data = {**data, **data.pop("meta")}
        return data

    @model_validator(mode="after")
    def decommission_rules(self):
        if self.status == "decommissioned":
            if self.decommissioned_at is None:
                raise ValueError("decommissioned servers need decommissioned_at")
            if self.replacement_site_code is None:
                raise ValueError("decommissioned servers need a replacement site code")
        return self`,
        mistakes: [
          "Mutating `self` in after-mode and forgetting to return it. The model comes back as None.",
          "Assuming before-mode always gets a dict. It receives whatever was passed — it could be an object, a list, anything. Guard with `isinstance`.",
          "Doing I/O in a validator. A database lookup inside a schema makes the model untestable and slow. Validate shape here; validate existence in the service layer.",
        ],
        exercise:
          "Write the decommission rule above, then try to express the same rule with `field_validator`. Notice what breaks and why.",
      },
      {
        id: "validationinfo",
        title: "ValidationInfo",
        explain:
          "Add a second parameter typed `ValidationInfo` to any validator and Pydantic hands you the run's context: `info.data` holds fields already validated, `info.field_name` tells you which field you are on, and `info.context` carries anything you passed into `model_validate(..., context=...)`. That last one is the supported way to inject request-scoped data without globals.",
        simple: `from pydantic import BaseModel, field_validator, ValidationInfo

class Range(BaseModel):
    low: int
    high: int

    @field_validator("high")
    @classmethod
    def above_low(cls, v: int, info: ValidationInfo) -> int:
        if "low" in info.data and v <= info.data["low"]:
            raise ValueError("high must exceed low")
        return v`,
        real: `from pydantic import BaseModel, field_validator, ValidationInfo

class Server(BaseModel):
    circle: str
    city: str

    @field_validator("city")
    @classmethod
    def city_in_circle(cls, v: str, info: ValidationInfo) -> str:
        allowed = (info.context or {}).get("cities_by_circle", {})
        circle = info.data.get("circle")
        if circle and allowed and v not in allowed.get(circle, set()):
            raise ValueError(f"{v!r} is not a city in circle {circle!r}")
        return v

Server.model_validate(
    {"circle": "MH", "city": "Pune"},
    context={"cities_by_circle": {"MH": {"Pune", "Mumbai", "Nagpur"}}},
)`,
        mistakes: [
          "Reading a field from `info.data` that is declared *below* the current field. It will not be there — validation follows declaration order.",
          "Assuming `info.data` contains fields that failed. It does not; failed fields are simply absent, so always use `.get()` or an `in` check.",
          "Using a module-level global instead of `context` to pass per-request data. That breaks the moment two requests run concurrently.",
        ],
        exercise:
          "Reorder the two fields so `city` is declared before `circle`. Run it and observe what `info.data` contains.",
      },
      {
        id: "annotated-validators",
        title: "Before, After, Plain and Wrap validators",
        explain:
          "The Annotated form of validation. Instead of decorating a method on one model, you attach the validator to a type and reuse it everywhere. Four variants differ only in where they sit relative to Pydantic's own parsing.",
        table: {
          head: ["Variant", "Runs", "Argument is", "Use it for"],
          rows: [
            ["BeforeValidator", "Before core parsing", "Raw input, any type", "Normalising: strip, uppercase, split a string into a list"],
            ["AfterValidator", "After core parsing", "The parsed, correctly-typed value", "Business rules. The default choice."],
            ["PlainValidator", "Instead of core parsing", "Raw input", "Types Pydantic cannot parse at all. Rare."],
            ["WrapValidator", "Around core parsing", "Raw input plus a handler", "Catching a core error and substituting a fallback"],
          ],
        },
        simple: `from typing import Annotated
from pydantic import BaseModel, BeforeValidator, AfterValidator

def strip_upper(v):
    return v.strip().upper() if isinstance(v, str) else v

def must_be_known(v: str) -> str:
    if v not in {"MH", "DL", "TN"}:
        raise ValueError(f"unknown circle {v!r}")
    return v

Circle = Annotated[str, BeforeValidator(strip_upper), AfterValidator(must_be_known)]

class Server(BaseModel):
    circle: Circle

Server(circle="  mh ").circle    # 'MH'`,
        real: `from typing import Annotated, Any
from pydantic import BaseModel, WrapValidator, BeforeValidator, ValidatorFunctionWrapHandler

def csv_to_list(v: Any):
    return v.split(",") if isinstance(v, str) else v

def default_on_bad_mtu(v: Any, handler: ValidatorFunctionWrapHandler) -> int:
    try:
        return handler(v)
    except ValueError:
        return 1500                    # legacy rows contain "" and "n/a"

Interfaces = Annotated[list[str], BeforeValidator(csv_to_list)]
LegacyMTU  = Annotated[int, WrapValidator(default_on_bad_mtu)]

class Server(BaseModel):
    interfaces: Interfaces = []
    mtu: LegacyMTU = 1500`,
        mistakes: [
          "Reaching for BeforeValidator when After would do. Before gives up the type guarantee — your function has to handle anything.",
          "PlainValidator on a type you also want documented. It replaces core validation entirely, so JSON-schema generation loses the underlying type unless you supply one.",
          "Swallowing every exception in a WrapValidator. Catch the specific failure you are compensating for, or you will hide real bugs.",
        ],
        exercise:
          "Write `Interfaces` as above, then rewrite it with AfterValidator instead and see why it cannot work.",
      },
      {
        id: "validation-error",
        title: "ValidationError",
        explain:
          "Every failure raises one `ValidationError` carrying a list of every problem found, not just the first. Each entry is a dict with `type`, `loc`, `msg`, `input` and a docs `url`. That `loc` tuple is the full path into nested data, and it is what lets you produce a per-field API response.",
        simple: `from pydantic import BaseModel, ValidationError

class Server(BaseModel):
    site_code: str
    api_port: int

try:
    Server(site_code=None, api_port="abc")
except ValidationError as e:
    print(e.error_count())        # 2 -- both problems, not just the first
    for err in e.errors():
        print(err["loc"], err["type"], err["msg"])`,
        real: `from pydantic import ValidationError

def to_api_errors(e: ValidationError) -> list[dict]:
    return [
        {
            "error": "VALIDATION_ERROR",
            "field": ".".join(str(p) for p in err["loc"]),
            "message": err["msg"],
            "code": err["type"],
        }
        for err in e.errors()
    ]`,
        mistakes: [
          "Returning `str(e)` to the client. It embeds the offending input, which is a real data-leak route for password and token fields.",
          "Reporting only `errors()[0]`. Clients end up fixing one field per round trip.",
          "Matching on `msg` text to branch behaviour. Messages are prose and change between releases — branch on `type` instead.",
        ],
        exercise:
          "Write `to_api_errors` and test it against a payload with three distinct failures, one of them inside a nested list.",
      },
      {
        id: "cross-field",
        title: "Cross-field validation",
        explain:
          "Rules that involve more than one field. `field_validator` plus `ValidationInfo.data` sees only the fields declared before it, while `model_validator(mode=\"after\")` sees everything. Unless you have a specific reason to fail early, the model validator is the correct home.",
        simple: `from pydantic import BaseModel, model_validator

class Maintenance(BaseModel):
    status: str
    window_start: str | None = None

    @model_validator(mode="after")
    def window_required_when_maintenance(self):
        if self.status == "maintenance" and self.window_start is None:
            raise ValueError("maintenance status requires window_start")
        return self`,
        real: `from pydantic import BaseModel, model_validator

class ServerCreate(BaseModel):
    site_type: str
    platform: str
    version: str
    replica_count: int = 1

    @model_validator(mode="after")
    def platform_rules(self):
        if self.site_type == "edge" and self.replica_count > 3:
            raise ValueError("edge sites support at most 3 replicas")
        if self.platform == "ocp" and not self.version.startswith("4."):
            raise ValueError("OCP versions start with 4.")
        return self`,
        mistakes: [
          "Putting the rule in a `field_validator` on the field declared first, where the other field is not yet available.",
          "Letting field order become load-bearing. If reordering two attributes breaks validation, the rule is in the wrong place.",
          "Encoding a rule that needs the database. \"This site_code must be unique\" is a service-layer check, not a schema check.",
        ],
        exercise:
          "Implement `platform_rules` with `field_validator` + `info.data`, then swap the field declaration order and watch it break.",
      },
      {
        id: "order",
        title: "Validation order",
        explain:
          "Knowing the order removes most of the mystery. Fields are processed in declaration order. For each field: before-validators run, then core parsing and constraints, then after-validators. Once all fields are done, model-level after-validators run. A field that fails is skipped — it will not appear in `info.data` for later fields, and its after-validators never run.",
        diagram: true,
        simple: `# For each field, in declaration order:
#
#   model_validator(mode="before")     once, on the raw input
#     |
#     v
#   BeforeValidator / field_validator(mode="before")
#     |
#     v
#   core parsing + Field() constraints
#     |
#     v
#   AfterValidator / field_validator(mode="after")
#
# then, once every field is done:
#
#   model_validator(mode="after")      once, on the built instance`,
        real: `from pydantic import BaseModel, field_validator, model_validator

class Trace(BaseModel):
    a: int
    b: int

    @field_validator("a", mode="before")
    @classmethod
    def _1(cls, v): print("1 before a"); return v

    @field_validator("a")
    @classmethod
    def _2(cls, v): print("2 after a"); return v

    @field_validator("b", mode="before")
    @classmethod
    def _3(cls, v): print("3 before b"); return v

    @model_validator(mode="after")
    def _4(self): print("4 model after"); return self

Trace(a=1, b=2)   # prints 1, 2, 3, 4 -- in that order`,
        mistakes: [
          "Expecting model-level after-validators to run when a field failed. They do not — the model was never built.",
          "Assuming before-validators for all fields run before any core parsing. They do not; the pipeline runs per field.",
        ],
        exercise:
          "Run the Trace model, then pass `a=\"bad\"` and see which of the four never print.",
      },
      {
        id: "strict-mode",
        title: "Strict mode in practice",
        explain:
          "Four levers, from narrowest to widest: `Field(strict=True)` on one field, `Annotated[int, Strict()]` on a reusable type, `ConfigDict(strict=True)` on a model, and `model_validate(..., strict=True)` on one call. The per-call form is the most useful in practice, because the same model often sits on both a lax boundary and a strict one.",
        simple: `from typing import Annotated
from pydantic import BaseModel, ConfigDict, Field, Strict

StrictPort = Annotated[int, Strict(), Field(ge=1, le=65535)]

class A(BaseModel):
    p: StrictPort

class B(BaseModel):
    model_config = ConfigDict(strict=True)
    p: int`,
        real: `class Server(BaseModel):
    site_code: str
    api_port: int

# From an HTTP query string -- everything is text, so stay lax
Server.model_validate({"site_code": "MH-PUN-04", "api_port": "8443"})

# From your own database row -- a string here means a schema bug
Server.model_validate(row, strict=True)`,
        mistakes: [
          "Enabling strict mode globally and then discovering every env var and query parameter fails.",
          "Assuming strict means the same thing for JSON and Python input. In JSON mode a date string is still accepted for a `date` field, because JSON has no date type.",
        ],
        exercise:
          "Validate one payload in all four strict configurations and record which inputs survive each.",
      },
    ],
  },
  {
    id: "serialization",
    n: 3,
    title: "Serialization and deserialization",
    blurb:
      "Getting data out is a first-class design problem, not an afterthought. Most API bugs live here.",
    concepts: [
      {
        id: "lifecycle",
        title: "The full lifecycle",
        explain:
          "Everything in this part is a step in one pipeline. Half of them move data in, the other half move it out, and the model sits in the middle as the only place your business logic is allowed to see.",
        diagram: "lifecycle",
        simple: `#  raw bytes / dict
#        |  model_validate_json()  /  model_validate()
#        v
#  parsing        -> is this even JSON? does it have the right keys?
#        |
#        v
#  validation     -> types, constraints, validators, cross-field rules
#        |
#        v
#  MODEL INSTANCE -> the only thing your business logic touches
#        |
#        v
#  serialization  -> model_dump(), aliases, computed fields
#        |
#        v
#  JSON response`,
        real: `@app.post("/servers", response_model=ServerResponse)
def create(payload: ServerCreate):
    row = repo.insert(payload)
    return ServerResponse.model_validate(row)`,
        mistakes: [
          "Doing business logic on the raw dict before validation.",
          "Returning the input model as the response model. They have different audiences and they will diverge.",
        ],
        exercise:
          "Trace one request through all six stages and write down which object exists at each — dict, model, ORM row, dict again.",
      },
      {
        id: "dump",
        title: "model_dump() and model_dump_json()",
        v2: "`.dict()` → `.model_dump()`, `.json()` → `.model_dump_json()`.",
        explain:
          "`model_dump()` produces a Python dict; `model_dump(mode=\"json\")` produces a dict containing only JSON-safe types; `model_dump_json()` produces a JSON string directly and is the fastest of the three because serialization happens in Rust without building an intermediate dict.",
        simple: `from datetime import datetime
from pydantic import BaseModel

class E(BaseModel):
    at: datetime

e = E(at=datetime(2026, 1, 1))

e.model_dump()                 # {'at': datetime.datetime(2026, 1, 1, 0, 0)}
e.model_dump(mode="json")      # {'at': '2026-01-01T00:00:00'}
e.model_dump_json()            # '{"at":"2026-01-01T00:00:00"}'`,
        real: `s.model_dump(
    by_alias=True,
    exclude_none=True,
    exclude_unset=True,
    exclude={"internal_note"},
)`,
        mistakes: [
          "Using `model_dump()` and then `json.dumps()` on the result. It fails on datetime and UUID — use `mode=\"json\"` or `model_dump_json()`.",
          "Confusing the three exclusions. `exclude_none` drops nulls, `exclude_unset` drops anything not supplied, `exclude_defaults` drops anything equal to its default.",
          "Forgetting `by_alias=True` and shipping snake_case to a camelCase client.",
        ],
        exercise:
          "Build a model with one unset field, one explicit None and one default value. Dump it with each of the three exclude flags and tabulate the results.",
      },
      {
        id: "validate",
        title: "model_validate() and model_validate_json()",
        v2: "`parse_obj()` → `model_validate()`, `parse_raw()` → `model_validate_json()`, `from_orm()` → `model_validate()` with `from_attributes=True`.",
        explain:
          "`model_validate()` takes a dict or an arbitrary object; `model_validate_json()` takes JSON bytes or a string. Prefer the JSON form when you have JSON — it parses and validates in one Rust pass.",
        simple: `Server.model_validate({"site_code": "MH-PUN-04", "api_port": 8443})
Server.model_validate_json(b'{"site_code":"MH-PUN-04","api_port":8443}')

# From an ORM object:
class Server(BaseModel):
    model_config = ConfigDict(from_attributes=True)
    site_code: str

Server.model_validate(sqlalchemy_row)`,
        real: `# Prefer model_validate_json over json.loads + model_validate:
# one pass, better error locations.`,
        mistakes: [
          "`json.loads()` followed by `model_validate()`. Two passes, and you lose the precise position of the failure.",
          "Forgetting `from_attributes=True` before passing an ORM row.",
        ],
        exercise:
          "Validate the same malformed JSON both ways and compare the error output.",
      },
      {
        id: "aliases",
        title: "Aliases: alias, validation_alias, serialization_alias",
        v2: "`allow_population_by_field_name` → `populate_by_name`. `AliasChoices` and `AliasGenerator` are new in v2.",
        explain:
          "`alias` sets both directions at once. When the wire format is asymmetric — you accept several spellings but emit exactly one — split it into `validation_alias` and `serialization_alias`. `AliasChoices` accepts any of several input names.",
        simple: `from pydantic import BaseModel, Field, ConfigDict, AliasChoices

class Server(BaseModel):
    model_config = ConfigDict(populate_by_name=True)

    site_code: str = Field(alias="siteCode")
    circle: str = Field(
        validation_alias=AliasChoices("circle", "telecomCircle", "circle_name"),
        serialization_alias="circle",
    )`,
        real: `from pydantic import BaseModel, ConfigDict
from pydantic.alias_generators import to_camel

class CamelModel(BaseModel):
    model_config = ConfigDict(
        alias_generator=to_camel,
        populate_by_name=True,
        extra="forbid",
        str_strip_whitespace=True,
    )

class ServerResponse(CamelModel):
    site_code: str
    site_type: str
    api_port: int

ServerResponse(site_code="MH-PUN-04", site_type="edge", api_port=8443)\\
    .model_dump(by_alias=True)
# {'siteCode': 'MH-PUN-04', 'siteType': 'edge', 'apiPort': 8443}`,
        mistakes: [
          "Setting an alias and forgetting `populate_by_name=True`. Every test that constructs by field name breaks.",
          "Forgetting `by_alias=True` at dump time.",
          "Using aliases to paper over a bad wire contract across dozens of models instead of writing one aliased base class.",
        ],
        exercise:
          "Write the CamelModel base, subclass it twice, and confirm both round-trip camelCase in and camelCase out while tests can still use snake_case.",
      },
      {
        id: "field-serializer",
        title: "field_serializer",
        explain:
          "The mirror of `field_validator`, for the way out. Register a function that controls how one field renders. The optional `when_used` argument lets you apply it only for JSON output, or only when the value is not None.",
        simple: `from datetime import datetime
from pydantic import BaseModel, field_serializer

class Event(BaseModel):
    at: datetime

    @field_serializer("at")
    def ser_at(self, v: datetime, _info) -> str:
        return v.strftime("%Y-%m-%d %H:%M")

Event(at=datetime(2026, 1, 1)).model_dump()   # {'at': '2026-01-01 00:00'}`,
        real: `from pydantic import BaseModel, field_serializer, SecretStr

class Server(BaseModel):
    site_code: str
    bmc_password: SecretStr

    @field_serializer("bmc_password", when_used="json")
    def hide(self, v: SecretStr) -> str:
        return "***"`,
        mistakes: [
          "Using a serializer to hide a secret that should never have been on the response model at all.",
        ],
        exercise:
          "Add a serializer that renders a `datetime` as a Unix timestamp only for JSON output.",
      },
      {
        id: "computed-field",
        title: "computed_field",
        v2: "New in v2. In v1 you faked this with a regular property plus manual dict manipulation.",
        explain:
          "A property that appears in the serialized output and in the JSON schema, but is not an input field. Perfect for values derived entirely from other fields.",
        simple: `from pydantic import BaseModel, computed_field

class Server(BaseModel):
    site_code: str
    api_port: int

    @computed_field
    @property
    def api_url(self) -> str:
        return f"https://{self.site_code.lower()}.example.net:{self.api_port}"`,
        real: `from datetime import datetime, timezone
from pydantic import BaseModel, computed_field

class Server(BaseModel):
    last_heartbeat: datetime

    @computed_field(description="Seconds since the last heartbeat")
    @property
    def heartbeat_age_s(self) -> int:
        return int((datetime.now(timezone.utc) - self.last_heartbeat).total_seconds())`,
        mistakes: [
          "Decorator order. `@computed_field` goes above `@property`, not below.",
          "Omitting the return annotation. Without it Pydantic cannot put the field in the JSON schema.",
          "Computing something expensive. This runs on every serialization.",
          "Expecting it to accept input. It is output-only.",
        ],
        exercise:
          "Add `is_stale` as a computed_field returning bool, dump the model twice, and confirm the value reflects real time.",
      },
    ],
  },
  {
    id: "advanced",
    n: 4,
    title: "Advanced model design",
    blurb:
      "The tools that decide whether a schema layer stays readable at forty models or collapses into copy-paste.",
    concepts: [
      {
        id: "configdict",
        title: "ConfigDict and model_config",
        v2: "`class Config:` is gone. Use `model_config = ConfigDict(...)`.",
        explain:
          "One dict controls model-wide behaviour, and it is inherited by subclasses — which is the mechanism behind every reusable base model you will write.",
        table: {
          head: ["Setting", "Does what", "Typical use"],
          rows: [
            ["from_attributes", "Allow validation from arbitrary objects", "SQLAlchemy rows"],
            ["populate_by_name", "Accept field name as well as alias", "Tests and internal callers"],
            ["extra", "ignore / forbid / allow", "forbid on request bodies"],
            ["frozen", "Immutable and hashable", "Value objects, dict keys"],
            ["strict", "Turn off coercion model-wide", "Internal boundaries"],
            ["validate_assignment", "Re-validate on attribute set", "Long-lived mutable objects"],
            ["str_strip_whitespace", "Strip every str field", "Human-entered form data"],
            ["alias_generator", "Derive aliases from field names", "camelCase APIs"],
          ],
        },
        simple: `from pydantic import BaseModel, ConfigDict

class StrictRequest(BaseModel):
    model_config = ConfigDict(extra="forbid", str_strip_whitespace=True)`,
        real: `from pydantic import BaseModel, ConfigDict
from pydantic.alias_generators import to_camel

class ApiModel(BaseModel):
    model_config = ConfigDict(
        alias_generator=to_camel,
        populate_by_name=True,
        extra="forbid",
        str_strip_whitespace=True,
    )

class DbModel(BaseModel):
    model_config = ConfigDict(from_attributes=True, extra="ignore")`,
        mistakes: [
          "Writing `class Config:` out of v1 habit. Pydantic v2 ignores it silently.",
          "`extra=\"allow\"` on a request body. It lets clients smuggle arbitrary keys into your model.",
        ],
        exercise:
          "Write `ApiModel`, subclass it, and confirm the config is inherited without repeating it.",
      },
      {
        id: "inheritance",
        title: "Inheritance and reusable base models",
        explain:
          "Subclassing inherits fields, validators and config. The standard shape for a CRUD resource is a `Base` holding the fields common to every direction, with thin subclasses adding what each direction needs.",
        simple: `class ServerBase(BaseModel):
    site_name: str
    site_code: str
    circle: str

class ServerCreate(ServerBase):
    pass

class ServerResponse(ServerBase):
    id: int
    created_at: datetime`,
        real: `class ServerBase(BaseModel):
    site_name: str
    site_code: str
    site_type: SiteType
    circle: str

class ServerCreate(ServerBase):
    model_config = ConfigDict(extra="forbid")

class ServerUpdate(BaseModel):
    # NOT a subclass -- every field must be optional
    site_name: str | None = None
    version: str | None = None

class ServerResponse(ServerBase):
    model_config = ConfigDict(from_attributes=True)
    id: int
    status: str
    created_at: datetime`,
        mistakes: [
          "Making `ServerUpdate` a subclass of `ServerBase`. You inherit required fields and then have to override every one to None.",
          "One giant `Server` model used for create, update, response and storage.",
        ],
        exercise:
          "Write all four models above, then try to express `ServerUpdate` as a subclass and list what goes wrong.",
      },
      {
        id: "discriminated",
        title: "Discriminated unions",
        explain:
          "A union where one field tells Pydantic which member to use. Without a discriminator, Pydantic tries every member and reports every failure. With one, it reads the tag, picks the right model, and reports only that model's errors.",
        simple: `from typing import Literal, Annotated
from pydantic import BaseModel, Field

class SSHCheck(BaseModel):
    kind: Literal["ssh"]
    port: int = 22

class HTTPCheck(BaseModel):
    kind: Literal["http"]
    url: str
    expect_status: int = 200

Check = Annotated[SSHCheck | HTTPCheck, Field(discriminator="kind")]

class Server(BaseModel):
    checks: list[Check] = []`,
        real: `# WITHOUT discriminator: 2 confusing errors for one bad payload.
# WITH discriminator: 1 precise error pointing to the right field.`,
        mistakes: [
          "A discriminator field that is not a `Literal` on every member.",
          "Different discriminator field names across members.",
          "Leaving a model union undiscriminated and fighting confusing multi-branch errors forever.",
        ],
        exercise:
          "Build the union above without the discriminator, submit a bad payload, then add the discriminator and compare the two error outputs.",
      },
      {
        id: "generics",
        title: "Generic models",
        v2: "`GenericModel` is gone. Subclass `BaseModel` and `Generic[T]` directly.",
        explain:
          "Parameterise a model over a type variable to describe a shape that repeats with different payloads. Paginated envelopes are the canonical case.",
        simple: `from typing import Generic, TypeVar
from pydantic import BaseModel

T = TypeVar("T")

class Page(BaseModel, Generic[T]):
    items: list[T]
    total: int
    page: int = 1

Page[Server].model_validate({"items": [...], "total": 42})`,
        real: `@app.get("/servers", response_model=Page[ServerResponse])
def list_servers(page: int = 1, size: int = 50):
    rows, total = repo.page(page, size)
    return Page[ServerResponse](items=rows, total=total, page=page, page_size=size)`,
        mistakes: [
          "Importing `GenericModel` from v1 muscle memory. It no longer exists.",
          "Using `Page[Any]` and losing every benefit.",
        ],
        exercise:
          "Write `Page[T]`, parameterise it with two different models, and diff the two generated JSON schemas.",
      },
    ],
  },
  {
    id: "typeadapter",
    n: 5,
    title: "TypeAdapter",
    blurb:
      "Everything BaseModel does, for types that are not models. Badly under-used.",
    concepts: [
      {
        id: "ta-what",
        title: "What TypeAdapter is and when to reach for it",
        v2: "New in v2. In v1 you had to wrap a bare type in a throwaway model with a `__root__` field.",
        explain:
          "TypeAdapter gives you validation, serialization and JSON-schema generation for any type annotation — a `list[str]`, a `dict[str, Server]`, a union, a dataclass, a TypedDict, a bare `int`. Build it once at module level, because construction compiles a validator and is the expensive part.",
        simple: `from pydantic import TypeAdapter

Codes = TypeAdapter(list[str])

Codes.validate_python(["MH-PUN-04", "DL-NDL-01"])
Codes.validate_json('["MH-PUN-04"]')
Codes.dump_json(["MH-PUN-04"])
Codes.json_schema()`,
        real: `# Build ONCE at import time
ServerList = TypeAdapter(list[Server])

def fetch_servers(resp) -> list[Server]:
    return ServerList.validate_json(resp.content)

# Anti-pattern -- recompiles the validator on every call:
def slow(resp):
    return TypeAdapter(list[Server]).validate_json(resp.content)`,
        table: {
          head: ["", "BaseModel", "TypeAdapter"],
          rows: [
            ["Shape", "Always an object with named fields", "Any type annotation at all"],
            ["Validate", "Model.model_validate(x)", "adapter.validate_python(x)"],
            ["Serialize", "instance.model_dump()", "adapter.dump_python(x)"],
            ["Schema", "Model.model_json_schema()", "adapter.json_schema()"],
            ["Reach for it when", "The thing is a domain entity", "You just need the type checked"],
          ],
        },
        mistakes: [
          "Constructing the adapter inside the function that uses it. Hoist it to module level.",
          "Writing a one-field wrapper model to validate a list. That is exactly what TypeAdapter removes.",
        ],
        exercise:
          "Validate a JSON array of 5,000 server dicts with a module-level TypeAdapter and with one constructed inside a loop. Time both.",
      },
      {
        id: "ta-examples",
        title: "TypeAdapter in practice",
        explain:
          "The cases that come up repeatedly: an external API returning a bare array, a config file that is a mapping, a union you need to resolve, and a plain dataclass you want validated.",
        simple: `from pydantic import TypeAdapter

# bare list of models
TypeAdapter(list[Server]).validate_python(rows)

# mapping with constrained values
TypeAdapter(dict[str, Annotated[int, Field(ge=0)]]).validate_python({"a": 1})

# discriminated union, no wrapper model
Check = Annotated[SSHCheck | HTTPCheck, Field(discriminator="kind")]
TypeAdapter(Check).validate_python({"kind": "ssh", "port": 22})`,
        real: `PrometheusTargets = TypeAdapter(list[Target])

async def scrape_targets(client, url: str) -> list[Target]:
    resp = await client.get(url)
    return PrometheusTargets.validate_json(resp.content)`,
        mistakes: [
          "Converting an external array into a wrapper model purely to validate it, then unwrapping it two lines later.",
          "Forgetting that `dump_json` returns bytes, not str.",
        ],
        exercise:
          "Take one external API you call and replace its manual `json.loads` plus hand-rolled checks with a module-level TypeAdapter.",
      },
    ],
  },
  {
    id: "dataclasses",
    n: 6,
    title: "Pydantic dataclasses",
    blurb:
      "Three ways to write a class with typed fields. Picking correctly is a two-minute decision that saves hours.",
    concepts: [
      {
        id: "three-way",
        title: "Standard dataclass vs Pydantic dataclass vs BaseModel",
        explain:
          "`pydantic.dataclasses.dataclass` is a drop-in for the stdlib decorator that adds validation. It keeps the dataclass interface while validating on construction. It exists mainly for retrofitting validation onto an existing dataclass-based codebase.",
        table: {
          head: ["", "stdlib dataclass", "pydantic dataclass", "BaseModel"],
          rows: [
            ["Validates on init", "No", "Yes", "Yes"],
            ["Coerces types", "No", "Yes", "Yes"],
            ["model_dump / model_validate", "No", "Via TypeAdapter", "Built in"],
            ["JSON schema", "No", "Yes", "Yes"],
            ["Positional arguments", "Yes", "Yes", "No — keyword only"],
            ["dataclasses.fields() / asdict()", "Yes", "Yes", "No"],
          ],
        },
        simple: `from dataclasses import dataclass as std_dataclass
from pydantic.dataclasses import dataclass as pyd_dataclass
from pydantic import BaseModel

@std_dataclass
class A:
    port: int

@pyd_dataclass
class B:
    port: int

class C(BaseModel):
    port: int

A(port="8443").port     # '8443'  -- no check
B(port="8443").port     # 8443    -- validated
C(port="8443").port     # 8443`,
        real: `# Decision rule:
#
#   Crosses a boundary (HTTP, config, external API, DB row)?
#       -> BaseModel
#
#   Internal object, never serialized, hot path?
#       -> stdlib dataclass
#
#   Existing dataclass codebase that now needs validation?
#       -> pydantic dataclass`,
        mistakes: [
          "Reaching for a pydantic dataclass on a new API schema. BaseModel has strictly more features there.",
          "Assuming `asdict()` behaves like `model_dump()`. Use `TypeAdapter(B).dump_python(obj)`.",
        ],
        exercise:
          "Take the same three-field class, write it all three ways, feed each a dict of strings, and record which coerce, which validate, and which can produce a JSON schema.",
      },
      {
        id: "pyd-dataclass-detail",
        title: "Working with Pydantic dataclasses",
        explain:
          "Config is passed to the decorator rather than set as a class attribute. For anything model-shaped — dumping, schema, JSON validation — you go through a TypeAdapter.",
        simple: `from pydantic import ConfigDict, Field, TypeAdapter
from pydantic.dataclasses import dataclass

@dataclass(config=ConfigDict(extra="forbid", str_strip_whitespace=True))
class RackSpec:
    rack_id: str
    units: int = Field(default=42, ge=1, le=60)

    def __post_init__(self):
        self.rack_id = self.rack_id.upper()

ta = TypeAdapter(RackSpec)
ta.validate_python({"rack_id": " r01 ", "units": "42"})
ta.json_schema()`,
        real: `# Retrofitting an existing module: change one import line.
#
#   - from dataclasses import dataclass
#   + from pydantic.dataclasses import dataclass
#
# Every existing construction site now validates.`,
        mistakes: [
          "Trying `model_config = ConfigDict(...)` as a class attribute. Pass `config=` to the decorator instead.",
          "Expecting `.model_dump()` on the instance. Use a TypeAdapter.",
        ],
        exercise:
          "Convert one stdlib dataclass to a pydantic dataclass and see which tests break. Each break is a place unvalidated data was flowing.",
      },
    ],
  },
];

const MIGRATION = [
  ["class Config:", "model_config = ConfigDict(...)", "Config classes are ignored silently"],
  [".dict()", ".model_dump()", ""],
  [".json()", ".model_dump_json()", ""],
  ["parse_obj()", "model_validate()", ""],
  ["parse_raw()", "model_validate_json()", ""],
  ["from_orm()", "model_validate() + from_attributes=True", ""],
  [".copy()", ".model_copy()", ""],
  ["schema()", "model_json_schema()", ""],
  ["@validator", "@field_validator", "Needs an explicit @classmethod"],
  ["@root_validator", "@model_validator", "after-mode returns self"],
  ["validator(pre=True)", 'field_validator(mode="before")', ""],
  ["Optional[x] implies default None", "Still required", "Highest-impact silent change"],
  ["Field(regex=...)", "Field(pattern=...)", ""],
  ["Field(min_items=...)", "Field(min_length=...)", ""],
  ["Field(allow_mutation=False)", "Field(frozen=True)", ""],
  ["Field(const=...)", "Literal[...]", "Removed"],
  ["__root__ field", "RootModel[...]", ""],
  ["GenericModel", "BaseModel + Generic[T]", "Removed"],
  ["orm_mode = True", "from_attributes=True", ""],
  ["allow_population_by_field_name", "populate_by_name", ""],
  ["update_forward_refs()", "model_rebuild()", "Rarely needed now"],
  ["int -> str coercion", "Rejected", "v2 is much stricter"],
  ["Union tries members left to right", "Smart mode picks the best match", ""],
  ["BaseSettings in pydantic", "pip install pydantic-settings", "Moved to its own package"],
];

const CHECKLIST = [
  {
    level: "Beginner",
    items: [
      "Define a BaseModel with required, optional and defaulted fields, and explain which is which without running it",
      "Predict what lax coercion will and will not accept",
      "Read a ValidationError and name the field that failed from the loc tuple",
      "Use Literal and Enum for closed sets",
      "Nest one model inside another and validate from a dict",
    ],
  },
  {
    level: "Intermediate",
    items: [
      "Choose correctly between field_validator, model_validator and an Annotated validator for a given rule",
      "Build reusable domain types with Annotated instead of repeating Field() constraints",
      "Explain the difference between exclude_none, exclude_unset and exclude_defaults from memory",
      "Set up camelCase aliases with populate_by_name and round-trip both directions",
      "Use computed_field and field_serializer to shape output without touching input",
    ],
  },
  {
    level: "Advanced",
    items: [
      "Design a Base/Create/Update/Response model family and justify why Update is not a subclass",
      "Use discriminated unions and show the error-message improvement over a plain union",
      "Write a generic Page[T] envelope and confirm the generated schemas",
      "Use TypeAdapter for non-model shapes and hoist adapters to module level",
      "Pick between stdlib dataclass, pydantic dataclass and BaseModel with a stated reason",
    ],
  },
  {
    level: "Production ready",
    items: [
      "Convert ValidationError into a stable API error contract that never leaks input values",
      "Apply extra=\"forbid\" on request bodies and extra=\"ignore\" on vendor payloads, deliberately",
      "Know which boundaries run strict and which run lax, and why",
      "Spot and remove redundant re-validation between layers",
      "Port v1 code without silently losing required-ness on Optional fields",
    ],
  },
];

const DOCS = [
  ["Concepts — Models", "https://docs.pydantic.dev/latest/concepts/models/"],
  ["Concepts — Fields", "https://docs.pydantic.dev/latest/concepts/fields/"],
  ["Concepts — Validators", "https://docs.pydantic.dev/latest/concepts/validators/"],
  ["Concepts — Serialization", "https://docs.pydantic.dev/latest/concepts/serialization/"],
  ["Concepts — Unions", "https://docs.pydantic.dev/latest/concepts/unions/"],
  ["Concepts — TypeAdapter", "https://docs.pydantic.dev/latest/concepts/type_adapter/"],
  ["Concepts — Dataclasses", "https://docs.pydantic.dev/latest/concepts/dataclasses/"],
  ["Migration guide (v1 to v2)", "https://docs.pydantic.dev/latest/migration/"],
];

const PY_KEYWORDS =
  "def|class|return|import|from|async|await|if|elif|else|for|in|while|try|except|finally|raise|with|as|None|True|False|not|and|or|is|lambda|pass|yield|assert|print";

const TOKEN_RX = new RegExp(
  "(#[^\\n]*)|(\"\"\"[\\s\\S]*?\"\"\"|'''[\\s\\S]*?'''|'[^'\\n]*'|\"[^\"\\n]*\")|(@[A-Za-z_][\\w.]*)|\\b(" +
    PY_KEYWORDS +
    ")\\b",
  "g"
);

function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function highlightPython(src) {
  return escapeHtml(src).replace(TOKEN_RX, (m, comment, str, dec, kw) => {
    if (comment) return '<span class="pyd-c">' + comment + "</span>";
    if (str) return '<span class="pyd-s">' + str + "</span>";
    if (dec) return '<span class="pyd-d">' + dec + "</span>";
    return '<span class="pyd-k">' + kw + "</span>";
  });
}

function Prose({ children }) {
  if (typeof children !== "string") return children || null;
  const parts = children.split("`");
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <code className="pyd-inline" key={i}>
            {p}
          </code>
        ) : (
          <React.Fragment key={i}>{p}</React.Fragment>
        )
      )}
    </>
  );
}

function CodeBlock({ label, src }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(src);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked */
    }
  }, [src]);

  return (
    <figure className="pyd-code">
      <figcaption className="pyd-code-bar">
        <span>{label}</span>
        <button type="button" className="pyd-copy" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
      </figcaption>
      <pre dangerouslySetInnerHTML={{ __html: highlightPython(src.trim()) }} />
    </figure>
  );
}

function DataTable({ head, rows }) {
  return (
    <div className="pyd-table-wrap">
      <table className="pyd-table">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j}>
                  <Prose>{c}</Prose>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrderDiagram() {
  return (
    <div className="pyd-diagram">
      <svg viewBox="0 0 700 176" role="img" aria-label="Validation order diagram">
        <defs>
          <marker id="pyd-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
            <path d="M0,0 L8,4 L0,8 z" fill="currentColor" />
          </marker>
        </defs>
        <g fontFamily="inherit" fontSize="11.5">
          <rect x="8" y="58" width="118" height="46" rx="4" className="pyd-svg-box" />
          <text x="67" y="78" textAnchor="middle" className="pyd-svg-t">model_validator</text>
          <text x="67" y="93" textAnchor="middle" className="pyd-svg-t2">mode="before"</text>
          <rect x="166" y="34" width="360" height="94" rx="4" className="pyd-svg-group" />
          <text x="346" y="24" textAnchor="middle" className="pyd-svg-t2">for each field, in declaration order</text>
          <rect x="180" y="58" width="100" height="46" rx="4" className="pyd-svg-box" />
          <text x="230" y="78" textAnchor="middle" className="pyd-svg-t">before</text>
          <text x="230" y="93" textAnchor="middle" className="pyd-svg-t2">raw input</text>
          <rect x="296" y="58" width="100" height="46" rx="4" className="pyd-svg-accent" />
          <text x="346" y="78" textAnchor="middle" className="pyd-svg-t">core parse</text>
          <text x="346" y="93" textAnchor="middle" className="pyd-svg-t2">+ constraints</text>
          <rect x="412" y="58" width="100" height="46" rx="4" className="pyd-svg-box" />
          <text x="462" y="78" textAnchor="middle" className="pyd-svg-t">after</text>
          <text x="462" y="93" textAnchor="middle" className="pyd-svg-t2">typed value</text>
          <rect x="566" y="58" width="124" height="46" rx="4" className="pyd-svg-box" />
          <text x="628" y="78" textAnchor="middle" className="pyd-svg-t">model_validator</text>
          <text x="628" y="93" textAnchor="middle" className="pyd-svg-t2">mode="after"</text>
          <g className="pyd-svg-line">
            <line x1="126" y1="81" x2="174" y2="81" markerEnd="url(#pyd-arrow)" />
            <line x1="280" y1="81" x2="291" y2="81" markerEnd="url(#pyd-arrow)" />
            <line x1="396" y1="81" x2="407" y2="81" markerEnd="url(#pyd-arrow)" />
            <line x1="526" y1="81" x2="560" y2="81" markerEnd="url(#pyd-arrow)" />
          </g>
          <text x="346" y="150" textAnchor="middle" className="pyd-svg-t2">
            a field that fails is skipped: it never reaches info.data, and the model is never built
          </text>
        </g>
      </svg>
    </div>
  );
}

function LifecycleDiagram() {
  const stages = [
    ["Raw input", "bytes, dict, ORM row"],
    ["Parsing", "model_validate_json()"],
    ["Validation", "types, constraints, validators"],
    ["Model instance", "the only thing business logic sees"],
    ["Serialization", "model_dump(), aliases, computed"],
    ["JSON response", "bytes on the wire"],
  ];
  return (
    <ol className="pyd-lifecycle">
      {stages.map(([a, b], i) => (
        <li key={a} className={i === 3 ? "pyd-lc-key" : undefined}>
          <span className="pyd-lc-t">{a}</span>
          <span className="pyd-lc-s">{b}</span>
        </li>
      ))}
    </ol>
  );
}

function Concept({ c, partN, i, done, onToggleDone }) {
  const [open, setOpen] = useState(true);

  return (
    <article className={"pyd-concept" + (done ? " pyd-is-done" : "")} id={c.id}>
      <header className="pyd-concept-head">
        <button
          type="button"
          className="pyd-disclose"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span className="pyd-num">
            {partN}.{i + 1}
          </span>
          <h3>{c.title}</h3>
        </button>
        <label className="pyd-done">
          <input type="checkbox" checked={done} onChange={() => onToggleDone(c.id)} />
          <span>done</span>
        </label>
      </header>

      {open && (
        <div className="pyd-concept-body">
          {c.v2 && (
            <p className="pyd-v2">
              <strong>v1 to v2</strong>
              <Prose>{c.v2}</Prose>
            </p>
          )}
          <p className="pyd-explain">
            <Prose>{c.explain}</Prose>
          </p>
          {c.diagram === "lifecycle" && <LifecycleDiagram />}
          {c.diagram === true && <OrderDiagram />}
          {c.table && <DataTable head={c.table.head} rows={c.table.rows} />}
          {c.simple && <CodeBlock label="Simple example" src={c.simple} />}
          {c.real && <CodeBlock label="Real-world example" src={c.real} />}
          {c.mistakes && (
            <div className="pyd-mistakes">
              <h4>Common mistakes</h4>
              <ul>
                {c.mistakes.map((m, n) => (
                  <li key={n}>
                    <Prose>{m}</Prose>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {c.exercise && (
            <div className="pyd-exercise">
              <h4>Mini exercise</h4>
              <p>
                <Prose>{c.exercise}</Prose>
              </p>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

const STORAGE_KEY = "pydantic-roadmap:done:v1";

export default function PydanticRoadmap() {
  const [query, setQuery] = useState("");
  const [done, setDone] = useState([]);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) setDone(JSON.parse(raw) || []);
    } catch {
      /* storage unavailable */
    }
  }, []);

  const toggleDone = useCallback((id) => {
    setDone((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return PARTS;
    return PARTS.map((p) => ({
      ...p,
      concepts: p.concepts.filter((c) =>
        (c.title + " " + c.explain + " " + (c.simple || "") + " " + (c.real || ""))
          .toLowerCase()
          .includes(q)
      ),
    })).filter((p) => p.concepts.length > 0);
  }, [q]);

  const total = PARTS.reduce((n, p) => n + p.concepts.length, 0);
  const pct = Math.round((done.length / total) * 100);

  return (
    <div className="pyd-root">
      <style>{CSS}</style>

      <header className="pyd-hero">
        <div className="pyd-hero-in">
          <div className="pyd-hero-left">
            <p className="pyd-kicker">Pydantic v2 &middot; parts 1&ndash;6</p>
            <h1>Describe the shape once. Let the boundary enforce it.</h1>
            <p className="pyd-lede">
              A hands-on roadmap through Pydantic v2, from what a field actually is to the model
              families you need for a production FastAPI service. Every concept carries an
              explanation, a simple example, a real example, the mistakes that cost people an
              afternoon, and something to go and try.
            </p>
          </div>
          <dl className="pyd-stats">
            <div>
              <dt>Concepts</dt>
              <dd>{total}</dd>
            </div>
            <div>
              <dt>Parts</dt>
              <dd>{PARTS.length}</dd>
            </div>
            <div>
              <dt>Written against</dt>
              <dd>2.13</dd>
            </div>
          </dl>
        </div>
      </header>

      <div className="pyd-toolbar">
        <div className="pyd-toolbar-in">
          <input
            type="search"
            className="pyd-search"
            placeholder="Filter concepts, e.g. alias, discriminated, strict"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Filter concepts"
          />
          <div className="pyd-progress" title={`${done.length} of ${total} marked done`}>
            <div className="pyd-progress-bar">
              <span style={{ width: `${pct}%` }} />
            </div>
            <span className="pyd-progress-n">
              {done.length}/{total}
            </span>
          </div>
          <button
            type="button"
            className="pyd-navtoggle"
            onClick={() => setNavOpen((v) => !v)}
            aria-expanded={navOpen}
          >
            Contents
          </button>
        </div>
      </div>

      <div className="pyd-layout">
        <nav className={"pyd-toc" + (navOpen ? " pyd-toc-open" : "")} aria-label="Contents">
          {PARTS.map((p) => (
            <div key={p.id} className="pyd-toc-part">
              <a href={`#${p.id}`} onClick={() => setNavOpen(false)}>
                <span className="pyd-toc-n">{p.n}</span>
                {p.title}
              </a>
              <ul>
                {p.concepts.map((c) => (
                  <li key={c.id}>
                    <a href={`#${c.id}`} onClick={() => setNavOpen(false)}>
                      {done.includes(c.id) && <span className="pyd-tick">&#10003;</span>}
                      {c.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="pyd-toc-part">
            <a href="#migration" onClick={() => setNavOpen(false)}>
              <span className="pyd-toc-n">v2</span>
              Migration reference
            </a>
          </div>
          <div className="pyd-toc-part">
            <a href="#checklist" onClick={() => setNavOpen(false)}>
              <span className="pyd-toc-n">&#10003;</span>
              Competency checklist
            </a>
          </div>
        </nav>

        <main className="pyd-main">
          {filtered.length === 0 && (
            <p className="pyd-empty">
              Nothing matches "{query}". Try a shorter term.
            </p>
          )}

          {filtered.map((p) => (
            <section key={p.id} id={p.id} className="pyd-part">
              <div className="pyd-part-head">
                <span className="pyd-part-n">{p.n}</span>
                <div>
                  <h2>{p.title}</h2>
                  <p>{p.blurb}</p>
                </div>
              </div>
              {p.concepts.map((c, i) => (
                <Concept
                  key={c.id}
                  c={c}
                  partN={p.n}
                  i={PARTS.find((x) => x.id === p.id).concepts.indexOf(c)}
                  done={done.includes(c.id)}
                  onToggleDone={toggleDone}
                />
              ))}
            </section>
          ))}

          {!q && (
            <>
              <section id="migration" className="pyd-part">
                <div className="pyd-part-head">
                  <span className="pyd-part-n">v2</span>
                  <div>
                    <h2>v1 to v2 migration reference</h2>
                    <p>
                      Most outdated tutorials fail on one of these. The last four rows are
                      behaviour changes rather than renames, which makes them the dangerous ones.
                    </p>
                  </div>
                </div>
                <DataTable
                  head={["Pydantic v1", "Pydantic v2", "Note"]}
                  rows={MIGRATION}
                />
              </section>

              <section id="checklist" className="pyd-part">
                <div className="pyd-part-head">
                  <span className="pyd-part-n">&#10003;</span>
                  <div>
                    <h2>Competency checklist</h2>
                    <p>
                      Scoped to parts 1&ndash;6. Each item is something you can demonstrate at a
                      keyboard, not something you can recognise in a list.
                    </p>
                  </div>
                </div>
                <div className="pyd-levels">
                  {CHECKLIST.map((lvl) => (
                    <div key={lvl.level} className="pyd-level">
                      <h3>{lvl.level}</h3>
                      <ul>
                        {lvl.items.map((it, i) => (
                          <li key={i}>
                            <Prose>{it}</Prose>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>

              <section className="pyd-part">
                <div className="pyd-part-head">
                  <span className="pyd-part-n">&#128218;</span>
                  <div>
                    <h2>Official documentation</h2>
                    <p>
                      Pydantic's own docs are versioned. Prefer them over blog posts, most of
                      which are still v1.
                    </p>
                  </div>
                </div>
                <ul className="pyd-docs">
                  {DOCS.map(([t, href]) => (
                    <li key={href}>
                      <a href={href} target="_blank" rel="noreferrer noopener">
                        {t}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const CSS = `
.pyd-root {
  --pyd-bg: #f6f6f8;
  --pyd-surface: #ffffff;
  --pyd-surface-2: #eceaf1;
  --pyd-ink: #1c1926;
  --pyd-ink-2: #5d5870;
  --pyd-line: #dedbe6;
  --pyd-line-2: #c8c3d4;
  --pyd-accent: #c2185b;
  --pyd-accent-bg: #fbe8ef;
  --pyd-warn: #8a5a00;
  --pyd-warn-bg: #fbf0d8;
  --pyd-ok: #1c6b4a;
  --pyd-ok-bg: #e0f0e7;
  --pyd-code-bg: #1c1926;
  --pyd-code-fg: #e6e2ee;
  --pyd-code-c: #8d86a3;
  --pyd-code-s: #ddb168;
  --pyd-code-k: #f06ba0;
  --pyd-code-d: #7fd6b0;
  background: var(--pyd-bg);
  color: var(--pyd-ink);
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}
[data-theme="dark"] .pyd-root {
  --pyd-bg: #121017;
  --pyd-surface: #1a1722;
  --pyd-surface-2: #241f30;
  --pyd-ink: #ece9f2;
  --pyd-ink-2: #a49dba;
  --pyd-line: #2e2839;
  --pyd-line-2: #40384f;
  --pyd-accent: #ff7fb2;
  --pyd-accent-bg: #2d1524;
  --pyd-warn: #e0b45f;
  --pyd-warn-bg: #2e2410;
  --pyd-ok: #6fd0a5;
  --pyd-ok-bg: #12301f;
  --pyd-code-bg: #0e0c13;
  --pyd-code-fg: #e6e2ee;
}
@media (prefers-color-scheme: dark) {
  .pyd-root:not([data-theme="light"] .pyd-root) {
    --pyd-bg: #121017;
    --pyd-surface: #1a1722;
    --pyd-surface-2: #241f30;
    --pyd-ink: #ece9f2;
    --pyd-ink-2: #a49dba;
    --pyd-line: #2e2839;
    --pyd-line-2: #40384f;
    --pyd-accent: #ff7fb2;
    --pyd-accent-bg: #2d1524;
    --pyd-warn: #e0b45f;
    --pyd-warn-bg: #2e2410;
    --pyd-ok: #6fd0a5;
    --pyd-ok-bg: #12301f;
    --pyd-code-bg: #0e0c13;
    --pyd-code-fg: #e6e2ee;
  }
}
.pyd-root *, .pyd-root *::before, .pyd-root *::after { box-sizing: border-box; }
.pyd-root h1, .pyd-root h2, .pyd-root h3, .pyd-root h4 { margin: 0; line-height: 1.16; letter-spacing: -0.021em; font-weight: 640; }
.pyd-root p { margin: 0 0 0.85rem; }
.pyd-root ul, .pyd-root ol { margin: 0; padding: 0; }
.pyd-root a { color: var(--pyd-accent); }
.pyd-root code, .pyd-root pre { font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace; }
.pyd-root :focus-visible { outline: 2px solid var(--pyd-accent); outline-offset: 2px; }
.pyd-inline { font-size: 0.86em; background: var(--pyd-surface-2); border: 1px solid var(--pyd-line); border-radius: 3px; padding: 0.08em 0.32em; white-space: nowrap; }
.pyd-hero { background: var(--pyd-surface); border-bottom: 1px solid var(--pyd-line); }
.pyd-hero-in { display: flex; align-items: center; gap: 2rem; justify-content: space-between; max-width: 100%; padding: 1rem 1.5rem; }
.pyd-hero-left { flex: 1 1 0; min-width: 0; }
.pyd-kicker { color: var(--pyd-accent); font-size: 0.78rem; font-weight: 600; margin-bottom: 0.3rem; }
.pyd-hero h1 { font-size: clamp(1.2rem, 2.4vw, 1.9rem); font-weight: 720; letter-spacing: -0.03em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pyd-lede { margin-top: 0.35rem; color: var(--pyd-ink-2); font-size: 0.88rem; margin-bottom: 0; }
.pyd-stats { display: flex; flex-wrap: wrap; gap: 1.25rem 2rem; flex: none; align-items: flex-start; border-left: 1px solid var(--pyd-line); padding-left: 2rem; }
.pyd-stats dt { font-size: 0.7rem; color: var(--pyd-ink-2); }
.pyd-stats dd { margin: 0.1rem 0 0; font-size: 1.4rem; font-weight: 700; letter-spacing: -0.02em; }
@media (max-width: 600px) { .pyd-hero-in { flex-direction: column; align-items: flex-start; } .pyd-stats { border-left: none; padding-left: 0; border-top: 1px solid var(--pyd-line); padding-top: 0.75rem; } .pyd-hero h1 { white-space: normal; } }
.pyd-toolbar { position: sticky; top: 0; z-index: 30; background: var(--pyd-bg); border-bottom: 1px solid var(--pyd-line); }
.pyd-toolbar-in { max-width: 100%; margin: 0 auto; padding: 0.5rem 1.5rem; display: flex; gap: 0.75rem; align-items: center; }
.pyd-search { flex: 1 1 auto; min-width: 0; font: inherit; font-size: 0.88rem; padding: 0.44rem 0.7rem; border: 1px solid var(--pyd-line-2); border-radius: 5px; background: var(--pyd-surface); color: var(--pyd-ink); }
.pyd-search::placeholder { color: var(--pyd-ink-2); }
.pyd-progress { display: flex; align-items: center; gap: 0.5rem; flex: none; }
.pyd-progress-bar { width: 82px; height: 5px; border-radius: 3px; background: var(--pyd-surface-2); overflow: hidden; }
.pyd-progress-bar span { display: block; height: 100%; background: var(--pyd-accent); transition: width 0.2s; }
.pyd-progress-n { font-size: 0.76rem; color: var(--pyd-ink-2); font-variant-numeric: tabular-nums; }
.pyd-navtoggle { display: none; font: inherit; font-size: 0.82rem; cursor: pointer; border: 1px solid var(--pyd-line-2); background: var(--pyd-surface); color: var(--pyd-ink); border-radius: 5px; padding: 0.4rem 0.7rem; }
.pyd-layout { width: 100%; padding: 0 1.5rem; display: grid; grid-template-columns: minmax(0,1fr); gap: 2.5rem; box-sizing: border-box; }
@media (min-width: 64rem) { .pyd-layout { grid-template-columns: 14rem minmax(0,1fr); gap: 2rem; } }
.pyd-toc { display: none; }
@media (min-width: 64rem) {
  .pyd-toc { display: block; position: sticky; top: 2.5rem; align-self: start; max-height: calc(100vh - 3rem); overflow-y: auto; padding: 1.25rem 0 2rem; font-size: 0.83rem; }
}
.pyd-toc.pyd-toc-open { display: block; padding: 1rem 0 1.5rem; border-bottom: 1px solid var(--pyd-line); }
.pyd-toc-part { margin-bottom: 1.15rem; }
.pyd-toc-part > a { display: flex; gap: 0.5rem; align-items: baseline; text-decoration: none; color: var(--pyd-ink); font-weight: 600; margin-bottom: 0.35rem; }
.pyd-toc-n { color: var(--pyd-accent); font-variant-numeric: tabular-nums; font-size: 0.78rem; min-width: 1.1rem; }
.pyd-toc ul { list-style: none; padding-left: 1.6rem; }
.pyd-toc li a { display: block; padding: 0.16rem 0; color: var(--pyd-ink-2); text-decoration: none; line-height: 1.35; }
.pyd-toc li a:hover { color: var(--pyd-accent); }
.pyd-tick { color: var(--pyd-ok); margin-right: 0.3rem; }
.pyd-main { padding: 2.5rem 0 5rem; min-width: 0; width: 100%; }
.pyd-part { margin-bottom: 4rem; scroll-margin-top: 4rem; }
.pyd-part-head { display: grid; grid-template-columns: auto 1fr; gap: 1rem; align-items: start; margin-bottom: 2rem; padding-bottom: 1.2rem; border-bottom: 2px solid var(--pyd-ink); }
.pyd-part-n { font-size: clamp(2rem, 5vw, 3rem); font-weight: 720; line-height: 0.8; color: var(--pyd-accent); letter-spacing: -0.04em; }
.pyd-part-head h2 { font-size: clamp(1.4rem, 3vw, 1.95rem); }
.pyd-part-head p { margin: 0.5rem 0 0; color: var(--pyd-ink-2); font-size: 0.94rem; }
.pyd-concept { border-top: 1px solid var(--pyd-line); padding: 1.35rem 0; scroll-margin-top: 4rem; }
.pyd-concept:last-child { border-bottom: 1px solid var(--pyd-line); }
.pyd-is-done { opacity: 0.58; }
.pyd-concept-head { display: flex; gap: 1rem; align-items: flex-start; }
.pyd-disclose { flex: 1; display: grid; grid-template-columns: auto 1fr; gap: 0.7rem; align-items: baseline; background: none; border: none; font: inherit; text-align: left; cursor: pointer; color: var(--pyd-ink); padding: 0; }
.pyd-disclose h3 { font-size: 1.1rem; }
.pyd-disclose:hover h3 { color: var(--pyd-accent); }
.pyd-num { color: var(--pyd-accent); font-size: 0.78rem; font-weight: 600; font-variant-numeric: tabular-nums; min-width: 1.8rem; }
.pyd-done { flex: none; display: inline-flex; gap: 0.35rem; align-items: center; font-size: 0.75rem; color: var(--pyd-ink-2); cursor: pointer; user-select: none; }
.pyd-done input { accent-color: var(--pyd-accent); width: 0.95rem; height: 0.95rem; }
.pyd-concept-body { padding: 0.9rem 0 0.4rem; width: 100%; }
@media (min-width: 40rem) { .pyd-concept-body { margin-left: 2.5rem; width: calc(100% - 2.5rem); } }
.pyd-explain { font-size: 0.98rem; }
.pyd-v2 { background: var(--pyd-warn-bg); border-radius: 4px; padding: 0.6rem 0.8rem; font-size: 0.87rem; margin-bottom: 1rem; }
.pyd-v2 strong { color: var(--pyd-warn); margin-right: 0.5rem; }
.pyd-code { margin: 1rem 0 1.2rem; border-radius: 6px; overflow: hidden; border: 1px solid var(--pyd-line); width: 100%; }
.pyd-code-bar { display: flex; justify-content: space-between; align-items: center; background: var(--pyd-surface-2); padding: 0.4rem 0.7rem; font-size: 0.74rem; color: var(--pyd-ink-2); border-bottom: 1px solid var(--pyd-line); }
.pyd-copy { font: inherit; font-size: 0.72rem; cursor: pointer; background: none; border: 1px solid var(--pyd-line-2); color: var(--pyd-ink-2); border-radius: 3px; padding: 0.1rem 0.45rem; }
.pyd-copy:hover { color: var(--pyd-accent); border-color: var(--pyd-accent); }
.pyd-code pre { margin: 0; padding: 0.9rem 1rem; overflow-x: auto; background: var(--pyd-code-bg); color: var(--pyd-code-fg); font-size: 0.795rem; line-height: 1.62; tab-size: 4; }
.pyd-c { color: var(--pyd-code-c); font-style: italic; }
.pyd-s { color: var(--pyd-code-s); }
.pyd-k { color: var(--pyd-code-k); }
.pyd-d { color: var(--pyd-code-d); }
.pyd-table-wrap { overflow-x: auto; margin: 1rem 0 1.3rem; }
.pyd-table { border-collapse: collapse; width: 100%; font-size: 0.85rem; min-width: 32rem; }
.pyd-table th, .pyd-table td { text-align: left; padding: 0.5rem 0.8rem 0.5rem 0; border-bottom: 1px solid var(--pyd-line); vertical-align: top; }
.pyd-table th { font-weight: 600; border-bottom-color: var(--pyd-line-2); white-space: nowrap; }
.pyd-table td:first-child { font-weight: 500; }
.pyd-mistakes, .pyd-exercise { border-radius: 5px; padding: 0.85rem 1rem; margin: 1.1rem 0; }
.pyd-mistakes { background: var(--pyd-accent-bg); }
.pyd-exercise { background: var(--pyd-ok-bg); }
.pyd-mistakes h4, .pyd-exercise h4 { font-size: 0.78rem; margin-bottom: 0.5rem; }
.pyd-mistakes h4 { color: var(--pyd-accent); }
.pyd-exercise h4 { color: var(--pyd-ok); }
.pyd-mistakes ul { list-style: none; font-size: 0.89rem; }
.pyd-mistakes li { padding-left: 1.05rem; position: relative; margin-bottom: 0.45rem; }
.pyd-mistakes li::before { content: "x"; position: absolute; left: 0; color: var(--pyd-accent); font-weight: 700; }
.pyd-exercise p { margin: 0; font-size: 0.89rem; }
.pyd-diagram { margin: 1.2rem 0; color: var(--pyd-ink-2); }
.pyd-diagram svg { width: 100%; height: auto; display: block; }
.pyd-svg-box { fill: var(--pyd-surface); stroke: var(--pyd-line-2); }
.pyd-svg-accent { fill: var(--pyd-accent-bg); stroke: var(--pyd-accent); }
.pyd-svg-group { fill: none; stroke: var(--pyd-line); stroke-dasharray: 4 3; }
.pyd-svg-t { fill: var(--pyd-ink); font-weight: 600; }
.pyd-svg-t2 { fill: var(--pyd-ink-2); font-size: 10px; }
.pyd-svg-line { stroke: currentColor; stroke-width: 1.4; }
.pyd-lifecycle { list-style: none; margin: 1.2rem 0; counter-reset: lc; }
.pyd-lifecycle li { counter-increment: lc; position: relative; padding: 0.55rem 0 0.55rem 2.4rem; border-left: 2px solid var(--pyd-line-2); margin-left: 0.6rem; }
.pyd-lifecycle li::before { content: counter(lc); position: absolute; left: -0.72rem; top: 0.62rem; width: 1.35rem; height: 1.35rem; border-radius: 50%; background: var(--pyd-surface); border: 1px solid var(--pyd-line-2); color: var(--pyd-ink-2); font-size: 0.7rem; display: grid; place-items: center; }
.pyd-lifecycle .pyd-lc-key { border-left-color: var(--pyd-accent); }
.pyd-lifecycle .pyd-lc-key::before { background: var(--pyd-accent); border-color: var(--pyd-accent); color: #fff; }
.pyd-lc-t { display: block; font-weight: 600; font-size: 0.92rem; }
.pyd-lc-s { display: block; color: var(--pyd-ink-2); font-size: 0.83rem; }
.pyd-levels { display: grid; gap: 1.5rem; }
@media (min-width: 52rem) { .pyd-levels { grid-template-columns: 1fr 1fr; gap: 2rem; } }
.pyd-level h3 { font-size: 0.95rem; color: var(--pyd-accent); padding-bottom: 0.4rem; border-bottom: 1px solid var(--pyd-line); margin-bottom: 0.7rem; }
.pyd-level ul { list-style: none; font-size: 0.89rem; }
.pyd-level li { padding-left: 1.15rem; position: relative; margin-bottom: 0.5rem; }
.pyd-level li::before { content: "[ ]"; position: absolute; left: 0; color: var(--pyd-ink-2); font-size: 0.75rem; }
.pyd-docs { list-style: none; font-size: 0.9rem; columns: 2; column-gap: 2rem; }
@media (max-width: 40rem) { .pyd-docs { columns: 1; } }
.pyd-docs li { margin-bottom: 0.4rem; break-inside: avoid; }
.pyd-empty { color: var(--pyd-ink-2); padding: 2rem 0; }
@media (max-width: 63.99rem) { .pyd-navtoggle { display: inline-block; } }
@media (prefers-reduced-motion: reduce) { .pyd-root * { transition: none !important; animation: none !important; } }
`;
